import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Icon,
  IconButton,
  CircularProgress,
  alpha,
  Paper,
  Alert,
  Chip,
} from "@mui/material";

export type ActionType =
  | "submit"
  | "approve"
  | "reject"
  | "reopen"
  | "delete"
  | "edit"
  | "custom";

interface ActionConfig {
  icon: string;
  color: string;
  bgColor: string;
  confirmText: string;
  loadingText: string;
  title: string;
  severity: "info" | "warning" | "error" | "success";
}

const actionConfigs: Record<ActionType, ActionConfig> = {
  submit: {
    icon: "send",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    confirmText: "Submit Request",
    loadingText: "Submitting...",
    title: "Submit Purchase Request",
    severity: "info",
  },
  approve: {
    icon: "check_circle",
    color: "#059669",
    bgColor: "#ECFDF5",
    confirmText: "Approve Request",
    loadingText: "Approving...",
    title: "Approve Purchase Request",
    severity: "success",
  },
  reject: {
    icon: "cancel",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    confirmText: "Reject Request",
    loadingText: "Rejecting...",
    title: "Reject Purchase Request",
    severity: "error",
  },
  reopen: {
    icon: "refresh",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    confirmText: "Reopen Request",
    loadingText: "Reopening...",
    title: "Reopen Purchase Request",
    severity: "warning",
  },
  delete: {
    icon: "delete",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    confirmText: "Delete Request",
    loadingText: "Deleting...",
    title: "Delete Purchase Request",
    severity: "error",
  },
  edit: {
    icon: "edit",
    color: "#0891B2",
    bgColor: "#F0F9FF",
    confirmText: "Continue",
    loadingText: "Loading...",
    title: "Edit Purchase Request",
    severity: "info",
  },
  custom: {
    icon: "help",
    color: "#64748B",
    bgColor: "#F1F5F9",
    confirmText: "Confirm",
    loadingText: "Processing...",
    title: "Confirm Action",
    severity: "info",
  },
};

interface ActionConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: ActionType;
  loading?: boolean;

  // Item details
  requestId?: string;
  itemName?: string;
  itemDescription?: string;

  // Custom overrides
  customTitle?: string;
  customMessage?: string;
  customConfirmText?: string;
  customIcon?: string;
  customColor?: string;

  // Additional data (for specific actions)
  extraInfo?: React.ReactNode;
  showWarning?: boolean;
  warningMessage?: string;
}

export const ActionConfirmationDialog: React.FC<
  ActionConfirmationDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  actionType,
  loading = false,
  requestId,
  itemName,
  itemDescription,
  customTitle,
  customMessage,
  customConfirmText,
  customIcon,
  customColor,
  extraInfo,
  showWarning = false,
  warningMessage,
}) => {
  const config = actionConfigs[actionType];
  const finalColor = customColor || config.color;
  const finalIcon = customIcon || config.icon;
  const finalTitle = customTitle || config.title;
  const finalConfirmText = customConfirmText || config.confirmText;

  // Keyboard shortcut handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open || loading) return;

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          onClose();
          break;
        case "Enter":
          if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
            event.preventDefault();
            onConfirm();
          }
          break;
        case "y":
        case "Y":
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            onConfirm();
          }
          break;
        case "c":
        case "C":
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            onClose();
          }
          break;
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, loading, onClose, onConfirm]);

  const getDefaultMessage = () => {
    switch (actionType) {
      case "submit":
        return `Are you sure you want to submit this purchase request? Once submitted, it cannot be edited until approved or rejected.`;
      case "approve":
        return `Are you sure you want to approve this purchase request? This will allow the requested items to be purchased.`;
      case "reject":
        return `Are you sure you want to reject this purchase request? The request will be marked as rejected and the requester will be notified.`;
      case "reopen":
        return `Are you sure you want to reopen this purchase request? This will allow the requester to make changes and resubmit.`;
      case "delete":
        return `Are you sure you want to delete this purchase request? This action cannot be undone and all associated data will be permanently deleted.`;
      case "edit":
        return `You are about to edit this purchase request. Any unsaved changes will be lost.`;
      default:
        return "Are you sure you want to proceed with this action?";
    }
  };

  const message = customMessage || getDefaultMessage();

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: finalColor,
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>{finalIcon}</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              {finalTitle}
            </Typography>
          </Box>
          {!loading && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: "24px !important" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: alpha(finalColor, 0.1),
              color: finalColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 32 }}>{finalIcon}</Icon>
          </Box>

          <Box>
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                fontFamily: "Montserrat",
                fontSize: "1rem",
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              {message}
            </Typography>

            {requestId && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  mt: 2,
                }}
              >
                Request ID: {requestId}
              </Typography>
            )}

            {itemName && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 1,
                }}
              >
                {itemName}
              </Typography>
            )}

            {itemDescription && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mt: 0.5,
                }}
              >
                {itemDescription}
              </Typography>
            )}
          </Box>

          {extraInfo && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                width: "100%",
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              {extraInfo}
            </Paper>
          )}

          {(showWarning ||
            actionType === "delete" ||
            actionType === "reject") && (
            <Alert
              severity={config.severity}
              sx={{
                width: "100%",
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: "1.25rem",
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {warningMessage ||
                  (actionType === "delete"
                    ? "This action cannot be undone"
                    : actionType === "reject"
                    ? "The requester will be notified"
                    : "Please confirm this action")}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Subtle keyboard shortcuts hint */}
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            textAlign: "center",
            fontSize: "0.7rem",
            mb: 1,
            fontFamily: "monospace",
          }}
        >
          Press Enter to confirm • Esc to cancel • Ctrl+Y for quick confirm
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            startIcon={<Icon>close</Icon>}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1,
              borderColor: "#E5E7EB",
              color: "#64748B",
              fontFamily: "Montserrat",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#94A3B8",
                bgcolor: "transparent",
              },
            }}
            title="Cancel (Esc)"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>{finalIcon}</Icon>
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1,
              bgcolor: finalColor,
              fontFamily: "Montserrat",
              fontWeight: 600,
              "&:hover": {
                bgcolor: alpha(finalColor, 0.8),
              },
              "&.Mui-disabled": {
                bgcolor: alpha(finalColor, 0.6),
                color: "white",
              },
            }}
            title="Confirm (Enter or Ctrl+Y)"
          >
            {loading ? config.loadingText : finalConfirmText}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
